import React, { useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { format } from "date-fns";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import "./ReportPeriodPicker.css";

registerLocale("ru", ru);

export const toNoonDate = (date) => {
  const nextDate = new Date(date);
  nextDate.setHours(12, 0, 0, 0);
  return nextDate;
};

export const getMonthStart = (date) =>
  toNoonDate(new Date(date.getFullYear(), date.getMonth(), 1));

export const getMonthEnd = (date) =>
  toNoonDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));

export const formatApiDate = (date) => {
  const offset = date.getTimezoneOffset();
  const adjustedDate = new Date(date.getTime() - offset * 60 * 1000);
  return adjustedDate.toISOString().split("T")[0];
};

export const createCurrentMonthPeriod = () => {
  const now = new Date();
  return {
    mode: "month",
    dateFrom: getMonthStart(now),
    dateTo: getMonthEnd(now),
  };
};

const formatMonthLabel = (date) => {
  const label = format(date, "LLLL yyyy", { locale: ru });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const formatDayLabel = (date) => format(date, "dd.MM.yyyy");

function ReportPeriodPicker({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftMode, setDraftMode] = useState(value.mode);
  const [rangeStart, setRangeStart] = useState(value.dateFrom);
  const [rangeEnd, setRangeEnd] = useState(value.dateTo);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const openPicker = () => {
    setDraftMode(value.mode);
    setRangeStart(value.dateFrom);
    setRangeEnd(value.dateTo);
    setIsOpen(true);
  };

  const triggerLabel =
    value.mode === "month"
      ? formatMonthLabel(value.dateFrom)
      : `${formatDayLabel(value.dateFrom)} — ${formatDayLabel(value.dateTo)}`;

  const handleMonthChange = (date) => {
    if (!date) return;
    onChange({
      mode: "month",
      dateFrom: getMonthStart(date),
      dateTo: getMonthEnd(date),
    });
    setIsOpen(false);
  };

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    const nextStart = start ? toNoonDate(start) : null;
    const nextEnd = end ? toNoonDate(end) : null;
    setRangeStart(nextStart);
    setRangeEnd(nextEnd);

    if (!nextStart || !nextEnd) return;

    const [dateFrom, dateTo] =
      nextStart <= nextEnd ? [nextStart, nextEnd] : [nextEnd, nextStart];
    onChange({ mode: "period", dateFrom, dateTo });
    setIsOpen(false);
  };

  return (
    <div className="report-period" ref={rootRef}>
      <button
        type="button"
        className={`report-period__trigger${isOpen ? " is-open" : ""}`}
        onClick={() => (isOpen ? setIsOpen(false) : openPicker())}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className="report-period__label">{triggerLabel}</span>
        <span className="report-period__chevron" aria-hidden="true">
          ▾
        </span>
      </button>
      {isOpen && (
        <div
          className="report-period__popover"
          role="dialog"
          aria-label="Выбор периода отчёта"
        >
          <div className="report-period__modes" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={draftMode === "month"}
              className={`report-period__mode${
                draftMode === "month" ? " is-active" : ""
              }`}
              onClick={() => setDraftMode("month")}
            >
              Месяц
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={draftMode === "period"}
              className={`report-period__mode${
                draftMode === "period" ? " is-active" : ""
              }`}
              onClick={() => setDraftMode("period")}
            >
              Период
            </button>
          </div>
          {draftMode === "month" ? (
            <DatePicker
              selected={value.dateFrom}
              onChange={handleMonthChange}
              showMonthYearPicker
              inline
              locale="ru"
              calendarClassName="report-period-calendar report-period-calendar--month"
            />
          ) : (
            <>
              <p className="report-period__hint">
                {rangeStart ? formatDayLabel(rangeStart) : "Дата начала"}
                {" — "}
                {rangeEnd ? formatDayLabel(rangeEnd) : "дата окончания"}
              </p>
              <DatePicker
                selectsRange
                startDate={rangeStart}
                endDate={rangeEnd}
                onChange={handleRangeChange}
                inline
                locale="ru"
                calendarClassName="report-period-calendar"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ReportPeriodPicker;
